import { Link } from "react-router-dom";
import { Label11, shortDate } from "@/components/common";
import { cn } from "@/lib/utils";
import { Eye, EyeOff, TrendingDown, TrendingUp, Minus } from "lucide-react";

// design_handoff_team_roles/screens/Overview.dc.html: the founder's Reliability and Delivery times cards (backend:
// GET /overview/insights; reliability.py, delivery.py). Systems and objectives, never people. The investor's view
// (pages/investor/Dashboard.jsx) builds its cards from the same pieces.

export const monthName = (m, opts = { month: "long" }) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", opts);
const hhmm = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

// "38 min", "1 h 5 min"
export function minutes(n) {
  if (n == null) return "—";
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60), m = n % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

// a step's time, from hours: "40 min", "5 h", "2.1 days"
export function duration(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 24) return `${Math.round(h)} h`;
  return `${(h / 24).toFixed(1)} days`;
}

export const ERROR_WORDS = {
  agent: "The agent stopped with an error", server: "The server didn't answer",
  provider: "The AI provider refused or failed", service: "GitHub or a tool server failed",
};
export const STATES = {
  done: ["Done", "text-gr-success"], review: ["In review", "text-gr-warning"],
  "in progress": ["In progress", "text-gr-ink-secondary"], todo: ["To do", "text-muted-foreground"],
};

export function CardHead({ title, note }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h2 className="m-0 font-display text-[15px] leading-[22px] tracking-[0.02em] font-semibold">{title}</h2>
      <span className="ms-auto text-sm text-muted-foreground">{note}</span>
    </div>
  );
}

export function Cells({ items, big }) {
  return (
    <div className="flex flex-wrap gap-px bg-gr-hairline border border-gr-hairline rounded-lg overflow-hidden tabular-nums">
      {items.map((c) => (
        <div key={c.k} className={cn("bg-card flex flex-col", big ? "flex-[1_1_150px] px-3.5 py-2.5" : "flex-[1_1_90px] px-3 py-2")}>
          <Label11>{c.k}</Label11>
          <span className={big ? "text-[24px] leading-7" : "text-[15px] leading-[22px]"}>{c.v}</span>
          {c.sub && <span className="text-sm text-muted-foreground">{c.sub}</span>}
        </div>
      ))}
    </div>
  );
}

export const rate = (n) => (n == null ? "—" : Number.isInteger(n) ? String(n) : n.toFixed(1));

export function Sparkline({ trend }) {
  const max = Math.max(...trend.map((t) => t.rate || 0), 1);
  const pts = trend.map((t, i) => (t.rate == null ? null : [4 + i * (152 / (trend.length - 1)), 4 + (1 - t.rate / max) * 36]));
  const shown = pts.filter(Boolean);
  if (shown.length < 2) return null;
  const last = shown[shown.length - 1];
  const words = trend.filter((t) => t.rate != null).map((t) => rate(t.rate)).join(", ");
  return (
    <div className="flex flex-col items-end gap-0.5">
      <svg width="160" height="44" viewBox="0 0 160 44" role="img" className="text-foreground"
        aria-label={`Errors per 100 turns, ${monthName(trend[0].month)} to ${monthName(trend.at(-1).month)}: ${words}`}>
        <polyline points={shown.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ")} fill="none" stroke="currentColor"
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={last[0]} cy={last[1]} r="3" fill="currentColor" />
      </svg>
      <span className="text-xs text-muted-foreground">{monthName(trend[0].month, { month: "short" })} to {monthName(trend.at(-1).month, { month: "short" })}</span>
    </div>
  );
}

export function Trend({ r }) {
  if (r.rate == null || r.previous_rate == null) return null;
  const before = monthName(r.previous_month);
  if (r.rate === r.previous_rate) return <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"><Minus className="w-3.5 h-3.5" />Same as {before}</span>;
  const down = r.rate < r.previous_rate;
  const Icon = down ? TrendingDown : TrendingUp;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm", down ? "text-gr-success" : "text-gr-warning")}>
      <Icon className="w-3.5 h-3.5" />{down ? "Down" : "Up"} from {rate(r.previous_rate)} in {before}
    </span>
  );
}

const outageSpan = (o) => {
  const start = shortDate(o.started_at, true);
  if (!o.ended_at) return `${start} to now`;
  return shortDate(o.ended_at) === shortDate(o.started_at) ? `${start} to ${hhmm(o.ended_at)}` : `${start} to ${shortDate(o.ended_at, true)}`;
};

// each error group in plain words, with a bar and its count
export function ErrorRows({ errors: counts }) {
  const errors = Object.entries(counts).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  const most = Math.max(...errors.map(([, n]) => n), 1);
  if (!errors.length) return null;
  return (
    <div className="flex flex-col text-sm tabular-nums">
      {errors.map(([group, n]) => (
        <div key={group} className="grid grid-cols-[minmax(0,1fr)_96px_24px] gap-3 items-center py-[7px] border-t">
          <span>{ERROR_WORDS[group] || group}</span>
          <span className="h-1 rounded-sm bg-gr-surface-2 overflow-hidden"><span className="block h-full bg-foreground" style={{ width: `${Math.round((n / most) * 100)}%` }} /></span>
          <span className="text-end">{n}</span>
        </div>
      ))}
    </div>
  );
}

// outages, downtime, the longest and how long a server takes to come back
export const outageCells = (r) => [
  { k: "Outages", v: String(r.outage_count) }, { k: "Downtime", v: minutes(r.downtime_min) },
  { k: "Longest", v: r.outage_count ? minutes(r.longest_min) : "—" },
  { k: "Back in", v: minutes(r.back_in_min), sub: r.back_in_min != null ? "on average" : null },
];

// whether investors see a card (FEATURE_INVESTORS): nothing when there are none
function SharedLine({ inv, k }) {
  if (!inv?.investors?.length) return null;
  const shared = inv.sharing[k];
  const Icon = shared ? Eye : EyeOff;
  return (
    <p className="m-0 flex items-center gap-2 text-sm text-muted-foreground"><Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span>{shared ? `Shared with your ${inv.investors.length === 1 ? "investor" : "investors"}.` : "Not shared with your investors."} <Link
        to="/founder/settings#investors" className="underline underline-offset-2 hover:opacity-50">Change</Link></span></p>
  );
}

export function ReliabilityCard({ r, inv }) {
  const empty = !r.outage_count && !r.turns;
  return (
    <section aria-label="Reliability" className="bg-card rounded-[10px] px-5 py-[18px] flex flex-col gap-3.5 min-w-0" data-testid="overview-reliability">
      <CardHead title="Reliability" note={`${monthName(r.month)} · systems only`} />
      {empty ? <span className="text-sm text-muted-foreground">No outages or errors yet. This fills in once sessions run.</span> : <>
        <Cells items={outageCells(r)} />
        {r.outages.length > 0 && (
          <div className="flex flex-col text-sm tabular-nums" data-testid="overview-outages">
            <span className="pb-1"><Label11>Server outages</Label11></span>
            {r.outages.slice(0, 5).map((o) => (
              <div key={o.started_at} className="flex justify-between gap-3 py-1.5 border-t">
                <span>{outageSpan(o)}</span><span className={o.ended_at ? "" : "text-gr-warning"}>{minutes(o.minutes)}{o.ended_at ? "" : " so far"}</span>
              </div>
            ))}
          </div>
        )}
        {r.turns > 0 ? <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-col tabular-nums">
              <Label11>Agent and service errors</Label11>
              <span><span className="text-[24px] leading-7">{rate(r.rate)}</span><span className="text-muted-foreground"> per 100 turns</span></span>
              <Trend r={r} />
            </div>
            <Sparkline trend={r.trend} />
          </div>
          <ErrorRows errors={r.errors} />
        </> : <span className="text-sm text-muted-foreground">No turns in {monthName(r.month)} yet.</span>}
      </>}
      <SharedLine inv={inv} k="reliability" />
    </section>
  );
}

export const STEP_COLS = "grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_repeat(3,minmax(0,0.9fr))] gap-3 px-3.5 py-2";

export function DeliveryCard({ d, inv }) {
  const investors = inv?.investors?.length > 0;
  const [company, ...projects] = d.medians;
  const before = monthName(d.previous_month);
  const was = (v) => (v == null ? `None in ${before}` : `${duration(v)} in ${before}`);
  return (
    <section aria-label="Delivery times" className="col-span-full bg-card rounded-[10px] px-5 py-[18px] flex flex-col gap-4 min-w-0" data-testid="overview-delivery">
      <CardHead title="Delivery times, per objective" note={`Medians for ${monthName(d.month)}, ${before} in grey`} />
      {d.objectives.length === 0 ? <span className="text-sm text-muted-foreground">No delivery times yet. Each objective shows its times once work starts.</span> : <>
        <Cells big items={[
          { k: "Time to start", v: duration(company.now.start), sub: was(company.before.start) },
          { k: "Time to first report", v: duration(company.now.first_report), sub: was(company.before.first_report) },
          { k: "Time to done", v: duration(company.now.done), sub: was(company.before.done) },
          { k: "Founder review time", v: duration(d.review), sub: `Company median · ${d.review_before == null ? `none in ${before}` : `${duration(d.review_before)} in ${before}`}` },
        ]} />
        <div className="overflow-x-auto border border-gr-hairline rounded-lg">
          <div className="min-w-[760px] text-sm tabular-nums">
            <div className={cn(STEP_COLS, "text-xs uppercase tracking-[0.09em] text-muted-foreground")}>
              <span>Objective</span><span>Project</span><span>State</span><span className="text-end">To start</span>
              <span className="text-end">To first report</span><span className="text-end">To done</span>
            </div>
            {projects.map((p) => (
              <div key={p.project_id} className={cn(STEP_COLS, "border-t bg-gr-surface-2 font-semibold")}>
                <span>{p.name}</span><span /><span className="font-normal text-muted-foreground">Median</span>
                {["start", "first_report", "done"].map((k) => (
                  <span key={k} className="text-end">{duration(p.now[k])} <span className="font-normal text-muted-foreground">· {duration(p.before[k])}</span></span>
                ))}
              </div>
            ))}
            {d.objectives.map((o) => {
              const [word, tone] = STATES[o.status] || STATES.todo;
              return (
                <div key={o.id} className={cn(STEP_COLS, "border-t items-center")}>
                  <span className="flex flex-col"><span className="text-[13px] leading-5">{o.title}</span>
                    {o.before_release && <span className="text-muted-foreground">From before the release</span>}</span>
                  <span className="text-muted-foreground">{o.project || "No project"}</span>
                  <span className={cn("text-xs uppercase tracking-[0.09em]", tone)}>{word}</span>
                  <span className="text-end">{duration(o.start)}</span>
                  <span className="text-end">{duration(o.first_report)}</span>
                  <span className="text-end">{duration(o.done)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </>}
      <p className="m-0 flex items-start gap-2 text-sm text-muted-foreground">
        <Eye className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
        <span>Times are per objective, never per person{investors ? `, here and for your ${inv.investors.length === 1 ? "investor" : "investors"}` : ""}. Founder review time runs from a report being ready to a founder opening it.
          {investors && <> <Link to="/founder/settings#investors" className="underline underline-offset-2 hover:opacity-50">Change what investors see</Link></>}</span>
      </p>
    </section>
  );
}
